'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { Atom, atom } from 'jotai';

import HybridSocket, { HybridSocketPhase } from '@flipster/socket/HybridSocket';
import { weakR230522SocketBunja } from '@flipster/socket/r230522/socket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { datadog } from '@flipster/utils/datadog';
import { InterfaceObjectNotFoundError } from '@flipster/webview-bridge/transport/discoverMessageTransportType';

import {
  bridgeR230522Socket,
  startBridgeConnectionDurationLogging,
  startBridgeConnectionErrorLogging,
} from './instance/bridge-socket';

export const DatadogR230522Setup = () => {
  useBunja(datadogR230522SetupBunja);
  return null;
};

const datadogR230522SetupBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket, socketStateAtom } = bunja.use(weakR230522SocketBunja);
  const { isHybridSocket, hybridPhaseAtom } = bunja.use(hybridR235022Bunja);

  const isBridgeSocket = r230522Socket === bridgeR230522Socket;
  const r230522Type = isHybridSocket ? 'hybrid' : isBridgeSocket ? 'bridge' : 'websocket';

  bunja.effect(() => datadog.setContext('r230522_type', r230522Type));
  syncDatadogContext('r230522_state', socketStateAtom);
  syncDatadogContext('r230522_hybrid_phase', hybridPhaseAtom);

  if (r230522Type === 'bridge') bunja.effect(startBridgeConnectionErrorLogging);
  else bunja.effect(() => startBridgeConnectionErrorLogging((error) => error instanceof InterfaceObjectNotFoundError));
  bunja.effect(startBridgeConnectionDurationLogging);

  function syncDatadogContext(contextName: string, atom: Atom<string>) {
    bunja.effect(() => {
      const setContext = () => datadog.setContext(contextName, store.get(atom));
      setContext();
      return store.sub(atom, setContext);
    });
  }
});

const hybridR235022Bunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);

  const isHybridSocket = r230522Socket instanceof HybridSocket;
  const hybridPhaseAtom = atom<HybridSocketPhase | 'no-hybrid'>(isHybridSocket ? r230522Socket.phase : 'no-hybrid');

  bunja.effect(() => {
    if (!isHybridSocket) return;
    return r230522Socket.on('phaseChanged', () => store.set(hybridPhaseAtom, r230522Socket.phase));
  });

  return { isHybridSocket, hybridPhaseAtom };
});
